import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsService } from "../services/cardsService";
import type { CardStatsRange, CardStatsResponse } from "../types/card-stats.types";

export interface UseCardStatsState {
  stats: CardStatsResponse | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCardStats(
  cardId: string | undefined,
  range: CardStatsRange,
  refreshKey?: number,
): UseCardStatsState {
  const [stats, setStats] = useState<CardStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Last read wins: a period picked while the previous one is still loading, or a refresh from a live push,
  // must never be overwritten by an older answer that lands later.
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    if (!cardId) {
      setStats(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await CardsService.fetchCardStats(cardId, range);
      if (seq !== seqRef.current) return;
      setStats(response.data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      const message = HttpApiService.getErrorMessage(err) || "Could not load card stats";
      setError(message);
    } finally {
      if (seq === seqRef.current) setIsLoading(false);
    }
  }, [cardId, range]);

  // refreshKey forces an in-place refetch without remounting the consumer.
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return { stats, isLoading, error, refresh: load };
}
